import type {FromBasePlayerInput,FromBaseRow} from '../../../compile/from-base-context';
import {fromBaseClone,fromBaseRecord,FromBaseInputMissing,FromBaseStageMissing} from '../../../compile/from-base-context';
import type {FromBasePlayerResourceMetadata} from '../../../compile/from-base-player-resources';
import {FROM_BASE_PLAYER_POST_FINALIZER_STAGES} from '../../../compile/from-base-player-resources';
import {createFromBasePlayerGrantContext} from '../../../compile/from-base-player-grants';
import {createFromBasePlayerSourceTypeReader} from '../../../compile/from-base-player-integers';
import {compileCreatureSenses} from '../../../compile/creature-senses-compile';
import {compileCreatureSpeeds} from '../../../compile/creature-speeds';
import {compileCreatureAttacks} from '../../../compile/creature-attacks';
import {readItemUuidSlot} from '../../../compile/inventory-raw-readers';
import {putIntegerCompilePath,removeIntegerCompilePath} from '../../../compile/integer-compute-slots';
import {computeSpellsPrepared} from '../core/compute/spells_prepared';
import {computeReach,type ReachDeps} from '../core/compute/reach';
import {createCatalogDefaultProviderReader} from '../../../compile/provider-catalog-order';
import {readNodeItemId} from '../../../compile/layer-cell-readers';
import {createDynamicIntegerSourceCollector} from '../../../compile/dynamic-integer-sources';
import {scoreCarrierIntegerLayer} from '../../../compile/formula-variable-resolution';
import {capInventorySourceRows} from '../../../compile/computed-persistence';
import {readCreaturePayloadStatuses} from '../../../compile/creature-status-reader';
import {json_encode} from '../../../php-compat';
import type {PreparedSpellDeps} from '../core/compute/spells_prepared';
import type {PortEnv} from '../../../port-contract';
import type {ItemFieldRow} from '../../../compile/item-field-reader';

/** Native post-finalizer compute windows, operating only on BASE + constructed
 * stages. Each partial compiler's named refusal remains a disclosed stage gap. */
export function constructPlayerClustersFromBase(base: FromBasePlayerInput,metadata: FromBasePlayerResourceMetadata,input: FromBaseRow,
    options: { prepared?: boolean } = {}) {
    if(!metadata.rollup)throw new FromBaseInputMissing('integer-rollup-metadata',base.entity_id);
    let host=fromBaseClone(input);
    const {definition,deps,readers}=createFromBasePlayerGrantContext(base,metadata,host,'post-finalizer-clusters');
    // Only the full entry supplies completed NPC definitions. Use the same first
    // active creature form as the stat graft, never the caster's ancestral base.
    const selected=metadata.definitionLookup && Object.values(readCreaturePayloadStatuses(host,readers.layerValue,definition))
        .find(status=>readers.itemField(status as ItemFieldRow,'item_type')==='creature');
    const source=selected ? readers.itemField(selected as ItemFieldRow,'status_arr') : null;
    const form=fromBaseRecord(source) ? source : null;
    const dependencies={...deps,...metadata.rollup,metadata:metadata.rollup,
        allEssences:metadata.preliminary.allEssences,queryTemplateDefinition:definition,
        queryItemType:createFromBasePlayerSourceTypeReader(base,metadata,host)};
    const done:string[]=[];
    const senses=compileCreatureSenses(host,dependencies,form);
    if(!senses.ok)throw new FromBaseStageMissing('post-finalizer-clusters',senses.reason);
    if(senses.senses)putIntegerCompilePath(host,['computed','creature','senses'],senses.senses);
    else removeIntegerCompilePath(host,['computed','creature','senses']);
    done.push(FROM_BASE_PLAYER_POST_FINALIZER_STAGES[0]);
    const speeds=compileCreatureSpeeds(host,dependencies,{form});
    if(!speeds.ok)throw new FromBaseStageMissing('post-finalizer-clusters',speeds.reason);
    putIntegerCompilePath(host,['computed','creature','speeds'],speeds.speeds);
    done.push(FROM_BASE_PLAYER_POST_FINALIZER_STAGES[1]);
    const attacks=compileCreatureAttacks(host,dependencies,form);
    if(!attacks.ok)throw new FromBaseStageMissing('post-finalizer-clusters',attacks.reason);
    if(attacks.attacks)putIntegerCompilePath(host,['computed','creature','attacks'],attacks.attacks);
    else removeIntegerCompilePath(host,['computed','creature','attacks']);
    if(attacks.combat)putIntegerCompilePath(host,['computed','creature','combat'],attacks.combat);
    else removeIntegerCompilePath(host,['computed','creature','combat']);
    done.push(FROM_BASE_PLAYER_POST_FINALIZER_STAGES[2]);

    const native=(value:unknown):ItemFieldRow|null=>{
        if(value==null)return null;
        const encoded=json_encode(value);if(encoded===false)throw new TypeError('Non-JSON port stage');
        return JSON.parse(encoded) as ItemFieldRow;
    };
    const {common}=createFromBasePlayerGrantContext(base,metadata,host,'post-finalizer-clusters');
    const providers=createCatalogDefaultProviderReader({...common,nodeItemId:readNodeItemId});
    const dynamic=createDynamicIntegerSourceCollector(dependencies);
    const reach:ReachDeps={
        layer_value:(row,key)=>readers.layerValue(native(row),key),
        item_arr_uuid_slot:readItemUuidSlot,
        _ft_default_provider_carriers:row=>providers.providers(native(row)),
        return_int_score_item_layer_type_name:(row,layer,plug)=>
            scoreCarrierIntegerLayer(native(row),layer,plug,false,metadata.rollup!,readers),
        ft_shapeshift_form_arr_for_speeds:()=>form,
        ft_compute_collect_dynamic_integer_sources:(row,layer,own,feats,form)=>
            dynamic.dynamic(native(row)!,layer,own,feats,native(form)),
        ft_cap_inventory_equipped_source_rows:rows=>capInventorySourceRows(native(rows)!),
    };
    const reachHost:{value:unknown}={value:host};
    computeReach(reachHost,{deps:reach} as PortEnv<ReachDeps>);
    host=native(reachHost.value) as FromBaseRow;
    done.push(FROM_BASE_PLAYER_POST_FINALIZER_STAGES[3]);
    if (options.prepared === false) return {row:host,completedStages:done};
    const callbacks:PreparedSpellDeps={
        layer_entries:(row,key)=>readers.layerEntries(native(row),key),
        item_arr_scalar_layer_slot:readItemUuidSlot,
        check_uuid:(id)=>typeof id==='string'&&/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(id),
        query_full_item_arr:(id)=>typeof id==='string'?definition(id):null,
        layer_has:(row,key)=>readers.layerValue(native(row),key)!==null||Object.values(readers.layerEntries(native(row),key)).length!==0,
        layer_value:(row,key)=>readers.layerValue(native(row),key),
        item_field:(row,key)=>readers.itemField(native(row),key),
    };
    const ref:{value:unknown}={value:host};
    // This stage consumes no clocks, randomness, identifiers or global state.
    const env={deps:callbacks} as PortEnv<PreparedSpellDeps>;
    computeSpellsPrepared(ref,env);
    const encoded=json_encode(ref.value);if(encoded===false)throw new TypeError('Non-JSON prepared-spell stage');
    host=JSON.parse(encoded) as FromBaseRow;
    done.push(FROM_BASE_PLAYER_POST_FINALIZER_STAGES[4]);
    return {row:host,completedStages:done};
}
